import React, { useEffect } from 'react';
import { redirect } from '../../../helper/redirectHelper';
import { Container, Entry, SubTitle, Title } from './PatentOntologyReferencePage.styled';
import Terms from '../../components/common/Terms';

const PatentOntologyReferencePage = () => {
    useEffect(() => {
        const handleAnchorClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            const targetId = (event.currentTarget as HTMLAnchorElement).getAttribute('href')?.substring(1);
            if (targetId) {
                const targetElement = document.getElementById(targetId);
                if (targetElement) {
                    event.preventDefault();
                    targetElement.scrollIntoView({ behavior: 'smooth' });
                }
            }
        };

        document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', handleAnchorClick as unknown as EventListener);
        });

        return () => {
            document.querySelectorAll('a[href^="#"]').forEach(anchor => {
                anchor.removeEventListener('click', handleAnchorClick as unknown as EventListener);
            });
        };
    }, []);
    return (
        <Container className="container">
            <Title data-testid="title">Patent Ontology Reference</Title>
            <div>
                <SubTitle data-testid="classes">Classes</SubTitle>
                <Entry id="Application">
                    <div className="heading">
                        <a href={redirect('def/patent/Application')}>Application</a>
                    </div>
                    <div>
                        The class of applications. An application may be an application for a patent, but it may also be
                        an application for other kinds of intellectual property rights. The applicationKind property
                        indicates the kind of application.
                    </div>
                </Entry>
                <Entry id="ApplicationKind">
                    <div className="heading">
                        <a href={redirect('def/patent/ApplicationKind')}>ApplicationKind</a>
                    </div>
                    <div>The kind of application, for example a patent application or a utility model application.</div>
                </Entry>
                <Entry id="Citation">
                    <div className="heading">
                        <a href={redirect('def/patent/Citation')}>Citation</a>
                    </div>
                    <div>
                        <div>
                            A citation in a patent document. Note that a citation is a distinct entity from the thing
                            cited. A citation provides information that enables the thing cited to be identified.
                        </div>
                        <div>
                            <span>sub classes </span>
                            <span>
                                <a href="#CitationNPL" data-testid="CitationNPL">CitationNPL</a>
                            </span>
                        </div>
                    </div>
                </Entry>
                <Entry id="CitationNPL">
                    <div className="heading">
                        <a href={redirect('def/patent/CitationNPL')}>CitationNPL</a>
                    </div>
                    <div>A citation of non-patent literature (NPL).</div>
                    <div>
                        super classes <a href="#Citation">Citation</a>
                    </div>
                </Entry>
                <Entry id="Family">
                    <div className="heading">
                        <a href={redirect('def/patent/Family')}>Family</a>
                    </div>
                    <div>
                        A patent family is a collection of patent applications or publications which are based on the
                        same invention.
                    </div>
                    <div>
                        sub classes <a href="#SimpleFamily">SimpleFamily</a>
                    </div>
                </Entry>
                <Entry id="Publication">
                    <div className="heading">
                        <a href={redirect('def/patent/Publication')}>Publication</a>
                    </div>
                    <div>An official publication of a patent document such as an A1 or a B1 publication.</div>
                    <div>
                        super classes <a href="#Record">Record</a>
                    </div>
                </Entry>
                <Entry id="PublicationKind">
                    <div className="heading">
                        <a href={redirect('def/patent/PublicationKind')}>PublicationKind</a>
                    </div>
                    <div>The class of patent publication kinds, such as A1, B1 etc.</div>
                </Entry>
                <Entry id="Record">
                    <div className="heading">
                        <a href={redirect('def/patent/Record')}>Record</a>
                    </div>
                    <div>The class of patent records. A patent record is a data record about a patent.</div>
                    <div>
                        sub classes <a href="#Publication">Publication</a>
                    </div>
                </Entry>
                <Entry id="RepresentationFormat">
                    <div className="heading">
                        <a href={redirect('def/patent/RepresentationFormat')}>RepresentationFormat</a>
                    </div>
                    <div>
                        A representation format is a data format in which a patent resource may be encoded for example
                        XML or PDF format
                    </div>
                </Entry>
                <Entry id="SimpleFamily">
                    <div className="heading">
                        <a href={redirect('def/patent/SimpleFamily')}>SimpleFamily</a>
                    </div>
                    <div>
                        A collection of applications with exactly the same priority or combination of priorities. EPO:
                        a.k.a. DOCDB family.
                    </div>
                    <div>
                        super classes <a href="#Family">Family</a>
                    </div>
                </Entry>
                <Entry id="VCardList">
                    <div className="heading">
                        <a href={redirect('def/patent/additionOf')}>VCardList</a>
                    </div>
                    <div>A list of VCards</div>
                </Entry>

                <SubTitle data-testid="properties">Properties</SubTitle>
                <Entry id="additionOf">
                    <div className="heading">
                        <a href={redirect('def/cpc/classification')}>additionOf</a>
                    </div>
                    <div>
                        The patent record with this property is an addition of the patent application referenced in this
                        property.
                    </div>
                    <div>
                        rdfs:domain <a href="#Record">Record</a>
                    </div>
                    <div>
                        rdfs:range <a href="#Application">Application</a>
                    </div>
                </Entry>
                <Entry id="agentVC">
                    <div className="heading">
                        <a href={redirect('def/patent/agentVC')}>agentVC</a>
                    </div>
                    <div>
                        The property agentVC relates a patent record to a vcard for a legal representative of one or
                        more of the applicants associated with a patent application.
                    </div>
                    <div>
                        rdfs:domain <a href="#Record">Record</a>
                    </div>
                    <div>
                        rdfs:range <a href="#VCard">VCard</a>
                    </div>
                </Entry>
                <Entry id="applicantVC">
                    <div className="heading">
                        <a href={redirect('def/patent/applicantVC')}>applicantVC</a>
                    </div>
                    <div>
                        The property applicantVC relates a patent record to a vcard for an applicant/proprietor for the
                        patent described by the record.{' '}
                    </div>
                    <div>
                        rdfs:domain <a href="#Record">Record</a>
                    </div>
                    <div>
                        rdfs:range <a href="#VCard">VCard</a>
                    </div>
                </Entry>
                <Entry id="application">
                    <div className="heading">
                        <a href={redirect('def/patent/application')}>application</a>
                    </div>
                    <div>
                        The property :application is a general purpose property that relates a resource to a patent
                        application.
                    </div>
                    <div>
                        rdfs:range <a href="#Application">Application</a>
                    </div>
                </Entry>
                <Entry id="applicationAuthority">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationAuthority')}>applicationAuthority</a>
                    </div>
                    <div>
                        The property applicationAuthority is a general purpose property that relates a resource, like a
                        patent application, to a patent authority.
                    </div>
                    <div>
                        rdfs:range <a href="#StateEntityOrIGO">StateEntityOrIGO</a>
                    </div>
                </Entry>
                <Entry id="applicationKind">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind')}>applicationKind</a>
                    </div>
                    <div>The property :level states the level of a node in the cooperative classification scheme.</div>
                    <div>
                        rdfs:range <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationNumber">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationNumber')}>applicationNumber</a>
                    </div>
                    <div>
                        The property applicationNumber relates a resource to a patent application number. In EPO the
                        application numbers have always 8 digits and optionally a dot with a checkdigit
                    </div>
                    <div>
                        rdfs:range <a href="https://www.w3.org/2001/XMLSchema#string">string</a>
                    </div>
                </Entry>
                <Entry id="applicationNumberEpodoc">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationNumberEpodoc')}>applicationNumberEpodoc</a>
                    </div>
                    <div>
                        The property applicationNumberEpodoc relates a resource to an Epodoc patent application number.
                    </div>
                    <div>
                        rdfs:range <a href="https://www.w3.org/2001/XMLSchema#string">string</a>
                    </div>
                </Entry>
                <Entry id="applicationNumberOriginal">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationNumberOriginal')}>applicationNumberOriginal</a>
                    </div>
                    <div>
                        The property applicationNumberOriginal relates a resource to a patent application number in
                        'original' format.
                    </div>
                    <div>
                        rdfs:range <a href="https://www.w3.org/2001/XMLSchema#string">string</a>
                    </div>
                </Entry>
                <Entry id="citation">
                    <div className="heading">
                        <a href={redirect('def/patent/citation')}>citation</a>
                    </div>
                    <div>
                        The property citation is a general purpose property that relates a resource to a citation.
                    </div>
                    <div>
                        rdfs:range <a href="Citation">Citation</a>
                    </div>
                    <div>
                        sub properties <a href="#citesPatentPublication">citesPatentPublication</a>{' '}
                        <a href="#citationNPL">citationNPL</a>
                    </div>
                </Entry>
                <Entry id="citationNPL">
                    <div className="heading">
                        <a href={redirect('def/patent/citationNPL')}>citationNPL</a>
                    </div>
                    <div>
                        The property citationNPL is a general purpose property that relates a resource to a non patent
                        literature citation.
                    </div>
                    <div>
                        rdfs:range <a href="#CitationNPL">CitationNPL</a>
                    </div>
                    <div>
                        super properties <a href="#citation">citation</a>{' '}
                    </div>
                </Entry>
                <Entry id="citationText">
                    <div className="heading">
                        <a href={redirect('def/patent/citationText')}>citationText</a>
                    </div>
                    <div>The property citationText relates a citation to the full text of the citation.</div>
                    <div>
                        rdfs:domain <a href="#Citation">Citation</a>
                    </div>
                </Entry>
                <Entry id="citesPatentPublication">
                    <div className="heading">
                        <a href={redirect('def/patent/citesPatentPublication')}>citesPatentPublication</a>
                    </div>
                    <div>
                        The property citesPatentPublication is a general purpose property that relates a resource to a
                        patent publication it cites.
                    </div>
                    <div>
                        rdfs:range <a href="#Publication">Publication</a>
                    </div>
                    <div>
                        super properties <a href="#citation">citation</a>{' '}
                    </div>
                </Entry>
                <Entry id="classificationCPCAdditional">
                    <div className="heading">
                        <a href={redirect('def/patent/classificationCPCAdditional')}>classificationCPCAdditional</a>
                    </div>
                    <div>
                        The property classificationCPCAdditional relates a resource to a CPC classification that is
                        related to the document but not a classification of the described invention.
                    </div>
                    <div>
                        rdfs:range <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="classificationCPCInventive">
                    <div className="heading">
                        <a href={redirect('def/patent/classificationCPCInventive')}>classificationCPCInventive</a>
                    </div>
                    <div>
                        The property classificationCPCInventive relates a resource to a CPC classification of the
                        invention it describes.
                    </div>
                    <div>
                        rdfs:range <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="classificationIPCAdditional">
                    <div className="heading">
                        <a href={redirect('def/patent/classificationIPCAdditional')}>classificationIPCAdditional</a>
                    </div>
                    <div>
                        The property classificationIPCAdditional relates a resource to an IPC classification that is
                        related to the document but not a classification of the described invention.
                    </div>
                    <div>
                        rdfs:range <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="classificationIPCInventive">
                    <div className="heading">
                        <a href={redirect('def/patent/classificationIPCInventive')}>classificationIPCInventive</a>
                    </div>
                    <div>
                        The property classificationIPCInventive relates a resource to an IPC classification of the
                        invention it describes.
                    </div>
                    <div>
                        rdfs:range <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="corrects">
                    <div className="heading">
                        <a href={redirect('def/patent/corrects')}>corrects</a>
                    </div>
                    <div>
                        The property corrects relates a patent record which is a correction to the patent record it
                        corrects.
                    </div>
                    <div>
                        rdfs:domain <a href="#Record">Record</a>
                        rdfs:range <a href="#Record">Record</a>
                    </div>
                </Entry>
                <Entry id="countryCode">
                    <div className="heading">
                        <a href={redirect('def/patent/countryCode')}>countryCode</a>
                    </div>
                    <div>
                        The property countryCode is a general purpose property that relates a resource to a string code
                        for a country. It is loosely defined in that it does not restrict the coding scheme used. It may
                        be used in vcard addresses to represent the code of a country rather than a name.
                    </div>
                    <div>
                        rdfs:range <a href="https://www.w3.org/2001/XMLSchema#string">string</a>
                    </div>
                </Entry>
                <Entry id="divisionalOf">
                    <div className="heading">
                        <a href={redirect('def/patent/divisionalOf')}>divisionalOf</a>
                    </div>
                    <div>
                        The patent record with this property is divided out from the patent application referenced in
                        this property.
                    </div>
                    <div>
                        rdfs:domain <a href="#Record">Record</a>
                        rdfs:range <a href="#Application">Application</a>
                    </div>
                </Entry>
                <Entry id="familyMember">
                    <div className="heading">
                        <a href={redirect('def/patent/familyMember')}>familyMember</a>
                    </div>
                    <div>The property familyMember relates a patent family to a member of that family.</div>
                    <div>
                        rdfs:domain <a href="#Family">Family</a>
                        rdfs:range <a href="#Application">Application</a>
                    </div>
                </Entry>
                <Entry id="familyMemberOf">
                    <div className="heading">
                        <a href={redirect('def/patent/familyMemberOf')}>familyMemberOf</a>
                    </div>
                    <div>The property familyMember relates a patent family to a member of that family.</div>
                    <div>
                        rdfs:domain <a href="#Application">Application</a>
                        rdfs:range <a href="#Family">Family</a>
                    </div>
                </Entry>
                <Entry id="filingDate">
                    <div className="heading">
                        <a href={redirect('def/patent/filingDate')}>filingDate</a>
                    </div>
                    <div>
                        The property filingDate relates a patent application to the date on which the application was
                        filed.
                    </div>
                    <div>
                        rdfs:range <a href="https://www.w3.org/2001/XMLSchema#date">date</a>
                    </div>
                </Entry>
                <Entry id="filingLanguage">
                    <div className="heading">
                        <a href={redirect('def/patent/filingLanguage')}>filingLanguage</a>
                    </div>
                    <div>
                        The property filingLanguage relates a patent record to the filing language of the patent
                        application the record describes.
                    </div>
                    <div>
                        rdfs:domain <a href="#Record">Record</a>
                        rdfs:range <a href="https://www.loc.gov/mads/rdf/v1#Language">Language</a>
                    </div>
                </Entry>
                <Entry id="grantDate">
                    <div className="heading">
                        <a href={redirect('def/patent/grantDate')}>grantDate</a>
                    </div>
                    <div>
                        The property grantDate relates the patent record to the date when the patent application has
                        been granted.
                    </div>
                    <div>
                        rdfs:range <a href="https://www.w3.org/2001/XMLSchema#date">date</a>
                    </div>
                </Entry>
                <Entry id="hasCorrection">
                    <div className="heading">
                        <a href={redirect('def/patent/hasCorrection')}>hasCorrection</a>
                    </div>
                    <div>The property hasCorrection relates a patent record to a correction of that patent record.</div>
                    <div>
                        rdfs:domain <a href="#Record">Record</a>
                        rdfs:range <a href="#Record">Record</a>
                    </div>
                </Entry>
                <Entry id="internationalApplication">
                    <div className="heading">
                        <a href={redirect('def/patent/internationalApplication')}>internationalApplication</a>
                    </div>
                    <div>
                        The property internationApplication relates a patent record to the international application at
                        WIPO for the patent application described by the record.
                    </div>
                    <div>
                        rdfs:domain <a href="#Record">Record</a>
                        rdfs:range <a href="#Application">Application</a>
                    </div>
                </Entry>
                <Entry id="inventorVC">
                    <div className="heading">
                        <a href={redirect('def/patent/inventorVC')}>inventorVC</a>
                    </div>
                    <div>
                        The property inventorVC relates a patent record to a vcard for an inventor of the patent
                        described by the record.
                    </div>
                    <div>
                        rdfs:domain <a href="#Record">Record</a>
                        rdfs:range <a href="https://www.w3.org/2006/vcard/ns#VCard">VCard</a>
                    </div>
                </Entry>
                <Entry id="priority">
                    <div className="heading">
                        <a href={redirect('def/patent/priority')}>priority</a>
                    </div>
                    <div>
                        The property priority relates a patent record to a a patent application which establishes
                        priority for the application described by the subject patent record.
                    </div>
                    <div>
                        rdfs:domain <a href="#Record">Record</a>
                        rdfs:range <a href="#Application">Application</a>
                    </div>
                </Entry>
                <Entry id="proceduralLanguage">
                    <div className="heading">
                        <a href={redirect('def/patent/proceduralLanguage')}>proceduralLanguage</a>
                    </div>
                    <div>
                        The property proceduralLanguage relates a patent record to the language used during the patent
                        granting procedure concerning the patent application described by the record.
                    </div>
                    <div>
                        rdfs:domain <a href="#Record">Record</a>
                        rdfs:range <a href="https://www.loc.gov/mads/rdf/v1#Language">Language</a>
                    </div>
                </Entry>
                <Entry id="publication">
                    <div className="heading">
                        <a href={redirect('def/patent/publication')}>publication</a>
                    </div>
                    <div>
                        The property publication is a general purpose property that relates a resource to a patent
                        publication.
                    </div>
                    <div>
                        rdfs:range <a href="#Publication">Publication</a>
                    </div>
                </Entry>
                <Entry id="publicationAuthority">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationAuthority')}>publicationAuthority</a>
                    </div>
                    <div>
                        The property publicationAuthority is a general purpose property that relates a resource to a
                        patent publication authority. It is used to relate a patent publication to the patent authority
                        publishing the publication.
                    </div>
                    <div>
                        rdfs:range <a href="#StateEntityOrIGO">StateEntityOrIGO</a>
                    </div>
                </Entry>
                <Entry id="publicationDate">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationDate')}>publicationDate</a>
                    </div>
                    <div>The property publicationDate relates a resource to the date of its publication.</div>
                    <div>
                        rdfs:range <a href="https://www.w3.org/2001/XMLSchema#date">date</a>
                    </div>
                </Entry>
                <Entry id="publicationKind">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind')}>publicationKind</a>
                    </div>
                    <div>The property publicationKind relates a patent publication to its kind, e.g. A1, B1 etc.</div>
                    <div>
                        rdfs:domain <a href="#Publication">Publication</a>
                        rdfs:range <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="publicationKindLabel">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKindLabel')}>publicationKindLabel</a>
                    </div>
                    <div>
                        The value of the property publicationKindLabel is natural language text that can be used to
                        present the type of patent publication to a reader. This text corresponds to INID code 12 of the
                        patent publications's bibliographic data.
                    </div>
                    <div>
                        rdfs:domain <a href="#Publication">Publication</a>
                        rdfs:range <a href="https://www.w3.org/2000/01/rdf-schema#Literal">Literal</a>
                    </div>
                </Entry>
                <Entry id="publicationNumber">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationNumber')}>publicationNumber</a>
                    </div>
                    <div>
                        The value of the property publicationKindLabel is natural language text that can be used to
                        present the type of patent publication to a reader. This text corresponds to INID code 12 of the
                        patent publications's bibliographic data.
                    </div>
                    <div>
                        rdfs:range <a href="https://www.w3.org/2001/XMLSchema#string">string</a>
                    </div>
                </Entry>
                <Entry id="representation">
                    <div className="heading">
                        <a href={redirect('def/patent/representation')}>representation</a>
                    </div>
                    <div>
                        The property representation is a general purpose property that relates a resource to an
                        alternative representation of that resource. It is used, for example, to relate publications to
                        the XML and PDF represenations of those publications.
                    </div>
                </Entry>
                <Entry id="representativeFamilyMember">
                    <div className="heading">
                        <a href={redirect('def/patent/representativeFamilyMember')}>representativeFamilyMember</a>
                    </div>
                    <div>
                        The property representativeFamilyMember relates a patent family to a representative member of
                        that family. It is often the case that a representative member can be used to obtain an
                        understanding of all the applications in the family.
                    </div>
                    <div>
                        rdfs:domain <a href="#Family">Family</a>
                        rdfs:range <a href="#Application">Application</a>
                    </div>
                </Entry>
                <Entry id="sourcePublication">
                    <div className="heading">
                        <a href={redirect('def/patent/sourcePublication')}>sourcePublication</a>
                    </div>
                    <div>
                        The property sourcePublication relates a non patent literature citation which is based on a
                        patent publication to the patent publication on which it is based.
                    </div>
                    <div>
                        rdfs:domain <a href="#CitationNPL">CitationNPL</a>
                        rdfs:range <a href="#Publication">Publication</a>
                    </div>
                </Entry>
                <Entry id="titleOfInvention">
                    <div className="heading">
                        <a href={redirect('def/patent/titleOfInvention')}>titleOfInvention</a>
                    </div>
                    <div>
                        The property titleOfInvention relates a patent record to the title of the invention. This
                        property corresponds to INID code 54 of the patent publications's bibliographic data.
                    </div>
                </Entry>
                <Entry id="vcAgentOf">
                    <div className="heading">
                        <a href={redirect('def/patent/vcAgentOf')}>vcAgentOf</a>
                    </div>
                    <div>The property vcAgentOf is the inverse of patent:agentVC.</div>
                    <div>
                        rdfs:range <a href="#Record">Record</a>
                    </div>
                </Entry>
                <Entry id="vcApplicantOf">
                    <div className="heading">
                        <a href={redirect('def/patent/vcApplicantOf')}>vcApplicantOf</a>
                    </div>
                    <div>The property vcAgentOf is the inverse of patent:agentVC.</div>
                    <div>
                        rdfs:range <a href="#Record">Record</a>
                    </div>
                </Entry>
                <Entry id="vcInventorOf">
                    <div className="heading">
                        <a href={redirect('def/patent/vcInventorOf')}>vcInventorOf</a>
                    </div>
                    <div>The property vcInventorOf is the inverse of patent:inventorVC.</div>
                    <div>
                        rdfs:range <a href="#Record">Record</a>
                    </div>
                </Entry>
                <SubTitle data-testid="individuals">Individuals</SubTitle>
                <Entry id="applicationKindScheme">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKindScheme')}>applicationKindScheme</a>
                    </div>
                    <div>The SKOS scheme of application kinds.</div>
                    <div>
                        type <a href="https://www.w3.org/2004/02/skos/core#ConceptScheme">ConceptScheme</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_A">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_A')}>applicationKind_A</a>
                    </div>
                    <div>A patent application</div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_D">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_D')}>applicationKind_D</a>
                    </div>
                    <div>A dummy application requiring automated action to correct.</div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_F">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_F')}>applicationKind_F</a>
                    </div>
                    <div>A design patent</div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_K">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_K')}>applicationKind_K</a>
                    </div>
                    <div>
                        This kind code is used to distinguish separate applications which otherwise have the same
                        identifiers.
                    </div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_L">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_L')}>applicationKind_L</a>
                    </div>
                    <div>
                        This kind code is used to distinguish separate applications which otherwise have the same
                        identifiers.
                    </div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_M">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_M')}>applicationKind_M</a>
                    </div>
                    <div>
                        This kind code is used to distinguish separate applications which otherwise have the same
                        identifiers.
                    </div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_N">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_N')}>applicationKind_N</a>
                    </div>
                    <div>
                        This kind code is used to distinguish separate applications which otherwise have the same
                        identifiers.
                    </div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_O">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_O')}>applicationKind_O</a>
                    </div>
                    <div>
                        This kind code is used to distinguish separate applications which otherwise have the same
                        identifiers.
                    </div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_P">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_P')}>applicationKind_P</a>
                    </div>
                    <div>A provisional application (US only)</div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_Q">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_Q')}>applicationKind_Q</a>
                    </div>
                    <div>A dummy application requiring manual action to correct.</div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_T">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_T')}>applicationKind_T</a>
                    </div>
                    <div>Applications which are "translations" of granted PCT or EP applications</div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_U">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_U')}>applicationKind_U</a>
                    </div>
                    <div>A utility model application</div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_V">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_V')}>applicationKind_V</a>
                    </div>
                    <div>Plant patent</div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="applicationKind_W">
                    <div className="heading">
                        <a href={redirect('def/patent/applicationKind_W')}>applicationKind_W</a>
                    </div>
                    <div>PCT application (in the international phase)</div>
                    <div>
                        type <a href="#ApplicationKind">ApplicationKind</a>
                    </div>
                </Entry>
                <Entry id="htmlFormat">
                    <div className="heading">
                        <a href={redirect('def/patent/htmlFormat')}>htmlFormat</a>
                    </div>
                    <div>The HTML document format.</div>
                    <div>
                        type <a href="#RepresentationFormat">RepresentationFormat</a>
                    </div>
                </Entry>
                <Entry id="patent">
                    <div className="heading">
                        <a href={redirect('def/patent')}>patent</a>
                    </div>
                    <div>
                        An ontology for representing information about patent applications, patents and patent
                        documents.
                    </div>
                    <div>
                        type <a href="https://www.w3.org/2002/07/owl#Ontology">Ontology</a>
                    </div>
                </Entry>
                <Entry id="pdfAFormat">
                    <div className="heading">
                        <a href={redirect('def/patent/pdfAFormat')}>pdfAFormat</a>
                    </div>
                    <div>PDF/A document format.</div>
                    <div>
                        type <a href="#RepresentationFormat">RepresentationFormat</a>
                    </div>
                </Entry>
                <Entry id="publicationKindScheme">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKindScheme')}>publicationKindScheme</a>
                    </div>
                    <div>A controlled vocabulary of patent document types.</div>
                    <div>
                        type <a href="https://www.w3.org/2004/02/skos/core#ConceptScheme">ConceptScheme</a>
                    </div>
                </Entry>
                <Entry id="publicationKind_A1">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind_A1')}>publicationKind_A1</a>
                    </div>
                    <div>
                        The publication kind A1. EPO: A1 publications contain the description of the patent application
                        as filed by the applicant together with the search report from the search authority
                    </div>
                    <div>
                        type <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="publicationKind_A2">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind_A2')}>publicationKind_A2</a>
                    </div>
                    <div>
                        The publication kind A2. EPO: A2 publications contain the description of the patent application
                        as filed by the applicant without any search report
                    </div>
                    <div>
                        type <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="publicationKind_A3">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind_A3')}>publicationKind_A3</a>
                    </div>
                    <div>
                        The publication kind A3. EPO: A3 publications contain the search report from the search
                        authority
                    </div>
                    <div>
                        type <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="publicationKind_A4">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind_A4')}>publicationKind_A4</a>
                    </div>
                    <div>
                        The publication kind A4. EPO: A4 publications contain a supplementary search report from the
                        European patent office
                    </div>
                    <div>
                        type <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="publicationKind_A8">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind_A8')}>publicationKind_A8</a>
                    </div>
                    <div>
                        The publication kind A8 is a correction of the titlepage (bibliographic data) of a patent
                        application
                    </div>
                    <div>
                        type <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="publicationKind_A9">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind_A9')}>publicationKind_A9</a>
                    </div>
                    <div>
                        The publication kind A9 is a correction of the complete publication of a patent application
                    </div>
                    <div>
                        type <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="publicationKind_B1">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind_B1')}>publicationKind_B1</a>
                    </div>
                    <div>
                        The publication kind B1. EPO: B1 publications contain the description of the granted patent
                        specification as agreed between the applicant and the examining division.
                    </div>
                    <div>
                        type <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="publicationKind_B2">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind_B2')}>publicationKind_B2</a>
                    </div>
                    <div>
                        The publication kind B2. EPO: B2 publications contain the amended description of the granted
                        patent specification as agreed between the applicant and the examining division after an
                        opposition proceeding.
                    </div>
                    <div>
                        type <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="publicationKind_B3">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind_B3')}>publicationKind_B3</a>
                    </div>
                    <div>
                        The publication kind B3. EPO: B3 publications contain the description of the granted patent
                        specification as agreed between the applicant and the examining division after a limitation
                        procedure.
                    </div>
                    <div>
                        type <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="publicationKind_B8">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind_B8')}>publicationKind_B8</a>
                    </div>
                    <div>
                        The publication kind B8 is a correction of the titlepage (bibliographic data) of a patent
                        specification (B publication).
                    </div>
                    <div>
                        type <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="publicationKind_B9">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind_B9')}>publicationKind_B9</a>
                    </div>
                    <div>
                        The publication kind B9 is a correction of a complete patent specification (B publication)
                    </div>
                    <div>
                        type <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="publicationKind_T">
                    <div className="heading">
                        <a href={redirect('def/patent/publicationKind_T')}>publicationKind_T</a>
                    </div>
                    <div>The publication kind T is a translation</div>
                    <div>
                        type <a href="#PublicationKind">PublicationKind</a>
                    </div>
                </Entry>
                <Entry id="representationFormatScheme">
                    <div className="heading">
                        <a href={redirect('def/patent/representationFormatScheme')}>representationFormatScheme</a>
                    </div>
                    <div>A skos scheme of patent data representation formats.</div>
                    <div>
                        type <a href="https://www.w3.org/2004/02/skos/core#ConceptScheme">ConceptScheme</a>
                    </div>
                </Entry>
                <Entry id="xmlFormat">
                    <div className="heading">
                        <a href={redirect('def/patent/xmlFormat')}>xmlFormat</a>
                    </div>
                    <div>The XML data format.</div>
                    <div>
                        type <a href="#RepresentationFormat">RepresentationFormat</a>
                    </div>
                </Entry>
            </div>
            <Terms />
        </Container>
    );
};
export default PatentOntologyReferencePage;